import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { addNewData, getAllListData } from 'services/CommonServices';
import {
  DisassociatedGroup,
  DisassociatedRow,
  DisassociationsResolveResult,
  DisassociationsState,
} from 'types';
import { TIMESHEET } from 'utils/Constant';

const staticState: DisassociationsState = {
  error: '',
  loading: false,
  resolveContext: null,
  resolveError: '',
  resolveResult: null,
  resolving: '',
  reviewing: null,
  rows: [],
  withDestinationOnly: false,
};

/**
 * The destination is resolved per week by the API, so a group can be partly resolvable. Grouping
 * happens here rather than server side because the list is one flat query - which is what lets it
 * sort by destination across every record instead of one page at a time.
 */
const groupByAssignment = (rows: DisassociatedRow[]): DisassociatedGroup[] => {
  const groups = new Map<string, DisassociatedGroup>();

  rows.forEach((row) => {
    const key = row.originalProjectConsultantId;
    const existing = groups.get(key);
    if (existing) {
      existing.weeks.push(row);
      return;
    }
    groups.set(key, {
      consultantName: row.consultantName ?? '',
      disassociatedAt: row.disassociatedAt,
      projectConsultantId: key,
      projectName: row.projectName ?? '',
      reason: row.reason ?? '',
      ultraStaffDbName: row.ultraStaffDbName,
      weeks: [row],
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      resolvableCount: group.weeks.filter(
        (week) => week.destinationProjectConsultantId,
      ).length,
      totalHours: group.weeks.reduce(
        (sum, week) =>
          sum +
          (Number(week.adjustmentHours) > 0
            ? Number(week.adjustmentHours)
            : Number(week.hours)),
        0,
      ),
    }))
    .sort((a, b) => {
      // Anything with somewhere to go comes first; then most recently disassociated.
      if (!!a.resolvableCount !== !!b.resolvableCount) {
        return a.resolvableCount ? -1 : 1;
      }
      return (
        moment(b.disassociatedAt).valueOf() -
        moment(a.disassociatedAt).valueOf()
      );
    });
};

export const useDisassociations = (options: { organization?: string }) => {
  const { organization } = options;
  const [state, setState] = useState<DisassociationsState>(staticState);

  const load = useCallback(() => {
    setState((prev) => ({ ...prev, error: '', loading: true }));
    const params = new URLSearchParams({ status: 'disassociated' });
    if (organization) {
      params.set('organization', organization);
    }
    getAllListData(
      `${String(TIMESHEET.TimeSheets)}/disassociations?${params.toString()}`,
      true,
    )
      .then((resp: DisassociatedRow[]) => {
        setState((prev) => ({
          ...prev,
          loading: false,
          rows: Array.isArray(resp) ? resp : [],
        }));
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          error: 'The disassociated timesheets could not be loaded.',
          loading: false,
        }));
      });
  }, [organization]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The group is kept aside before the reload wipes it - the result message names the consultant,
   * the assignment and the weeks that moved, so the record stands on its own once the row is gone.
   */
  const resolve = (group: DisassociatedGroup) => {
    setState((prev) => ({
      ...prev,
      resolveError: '',
      resolving: group.projectConsultantId,
    }));
    addNewData(
      { sourceProjectConsultantId: group.projectConsultantId },
      `${String(TIMESHEET.TimeSheets)}/disassociations/restorations`,
      undefined,
      false,
    )
      .then((resp: DisassociationsResolveResult) => {
        setState((prev) => ({
          ...prev,
          resolveContext: group,
          resolveResult: resp,
          resolving: '',
          reviewing: null,
        }));
        load();
      })
      .catch((err: unknown) => {
        setState((prev) => ({
          ...prev,
          resolveError:
            typeof err === 'string' && err
              ? err
              : 'Nothing was changed. Reload the page to see the current state.',
          resolving: '',
        }));
      });
  };

  const toggleWithDestinationOnly = () =>
    setState((prev) => ({
      ...prev,
      withDestinationOnly: !prev.withDestinationOnly,
    }));

  const dismissResult = () =>
    setState((prev) => ({
      ...prev,
      resolveContext: null,
      resolveError: '',
      resolveResult: null,
    }));

  const openReview = (group: DisassociatedGroup) =>
    setState((prev) => ({ ...prev, reviewing: group }));

  const closeReview = () => setState((prev) => ({ ...prev, reviewing: null }));

  const allGroups = groupByAssignment(state.rows);
  const groups = state.withDestinationOnly
    ? allGroups.filter((group) => group.resolvableCount > 0)
    : allGroups;
  const resolvableGroups = allGroups.filter(
    (group) => group.resolvableCount > 0,
  ).length;

  return {
    closeReview,
    dismissResult,
    groups,
    openReview,
    reload: load,
    resolvableGroups,
    resolve,
    state,
    toggleWithDestinationOnly,
    totalGroups: allGroups.length,
  };
};

export type UseDisassociations = ReturnType<typeof useDisassociations>;
